import { API_ENDPOINTS } from '../constants/api';
import apiClient from '../config/axiosInterceptors';
import { ResponseHandler } from '../config/responseHandler';
import { getSignedUrls } from '../utils/fileOperations';
import { isUserAdmin } from '../utils/function';
import { reportError } from '../sentry';

class UserService {
  /**
   * Update the current user's FCM token
   * @param {string} fcmToken
   * @returns {Promise<Object>} Update response
   */
  async updateFcmToken(fcmToken) {
    if (isUserAdmin()) {
      return {
        success: true,
        skipped: true,
        message: 'FCM token update skipped for admin users',
        data: null,
      };
    }

    try {
      const response = await apiClient.patch(API_ENDPOINTS.UPDATE_FCM_TOKEN, { fcmToken });
      return ResponseHandler.handleSuccess(response, 'FCM token updated successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to update FCM token');
    }
  }

  /**
   * Update last login time (authenticated session activity).
   * @returns {Promise<Object>} Update response
   */
  async updateLoginTime() {
    try {
      const response = await apiClient.patch(API_ENDPOINTS.USER_LOGIN_TIME, {});
      return ResponseHandler.handleSuccess(response, 'Login time updated successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to update login time');
    }
  }

  /**
   * Notify backend about an incoming/missed call event.
   * @param {{ otherCallUserId: string, callNotificationType?: 'incoming' | 'missed', callType?: string, type?: 'audio' | 'video', roomId?: string, pricingModel?: 'per_minute' | 'flat_rate', amount?: number, creditsPerMinute?: number }} payload - amount/creditsPerMinute are dollar values for call notify emails.
   * @returns {Promise<Object>} Notify response
   */
  async notifyIncomingCall(payload) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.USER_INCOMING_CALL_NOTIFY, payload);
      return ResponseHandler.handleSuccess(response, 'Incoming call notify sent successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to send incoming call notify');
    }
  }

  /**
   * Fetch user account information
   * @returns {Promise<Object>} User data response
   */
  async fetchUser() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.FETCH_USER);
      
      if (response.data?.data) {
        const userData = response.data.data;
        
        // Process profile picture with signed URL if it exists
        if (userData.profile_picture) {
          const profilePictureKey = userData.profile_picture;
          // Check if it's already a full URL (signed URL) or a file key
          if (profilePictureKey && typeof profilePictureKey === 'string' && !profilePictureKey.startsWith('http')) {
            try {
              // Get signed URL for the profile picture
              const signedUrlsData = await getSignedUrls([profilePictureKey], true);
              if (signedUrlsData && signedUrlsData.length > 0) {
                // Replace the file key with signed URL
                userData.profile_picture = signedUrlsData[0].signedUrl;
              }
            } catch (error) {
              reportError(error, 'signed URL profile picture');
              // Keep the original value if signed URL fetch fails
            }
          }
        }

        // Process profile banner with signed URL if it exists
        if (userData.profile_banner) {
          const profileBannerKey = userData.profile_banner;
          
          // Check if it's already a full URL (signed URL) or a file key
          if (profileBannerKey && typeof profileBannerKey === 'string' && !profileBannerKey.startsWith('http')) {
            try {
              // Get signed URL for the profile banner
              const signedUrlsData = await getSignedUrls([profileBannerKey], true);
              if (signedUrlsData && signedUrlsData.length > 0) {
                // Replace the file key with signed URL
                userData.profile_banner = signedUrlsData[0].signedUrl;
              }
            } catch (error) {
              reportError(error, 'signed URL profile banner');
              // Keep the original value if signed URL fetch fails
            }
          }
        }
        
        return ResponseHandler.handleSuccess(response, 'User data fetched successfully', userData);
      } else {
        return ResponseHandler.handleError(
          { message: 'No user data found', status: 404 },
          'User data not found'
        );
      }
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch user data');
    }
  }

  /**
   * Update user information
   * @param {Object} userData - User data to update
   * @returns {Promise<Object>} Update response
   */
  async updateUser(userData) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.UPDATE_USER, userData);
      return ResponseHandler.handleSuccess(response, 'User updated successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to update user');
    }
  }

  /**
   * Delete user account
   * @param {string} email - User email to delete
   * @returns {Promise<Object>} Delete response
   */
  async deleteUser(email) {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.DELETE_USER, {
        data: { email }
      });
      return ResponseHandler.handleSuccess(response, 'User deleted successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to delete user');
    }
  }

  /**
   * Update user profile picture
   * @param {string} fileKey - S3 file key for the profile picture
   * @returns {Promise<Object>} Update response
   */
  async updateProfileImage(fileKey) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.UPDATE_PROFILE_IMAGE, {
        file: fileKey
      });
      return ResponseHandler.handleSuccess(response, 'Profile image updated successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to update profile image');
    }
  }

  /**
   * Update user profile banner
   * @param {string} fileKey - S3 file key for the profile banner
   * @returns {Promise<Object>} Update response
   */
  async updateProfileBanner(fileKey) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.UPDATE_PROFILE_BANNER, {
        file: fileKey
      });
      return ResponseHandler.handleSuccess(response, 'Profile banner updated successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to update profile banner');
    }
  }

  /**
   * Fetch featured profiles for authenticated users
   * @param {Object} params - Query parameters object
   * @param {number} params.limit - Number of featured profiles to fetch
   * @param {string} params.category - Optional category filter
   * @param {string} params.sort - Optional sort parameter
   * @returns {Promise<Object>} Featured profiles response with signed URLs
   */
  async getFeaturedProfiles(params = {}) {
    try {
      // Set default limit if not provided (show all featured)
      const queryParams = { limit: 50, ...params };
      
      // Build query string from params object
      const queryString = Object.keys(queryParams)
        .filter(key => queryParams[key] !== undefined && queryParams[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
        .join('&');
      
      const url = queryString 
        ? `${API_ENDPOINTS.FEATURED_PROFILE}?${queryString}`
        : API_ENDPOINTS.FEATURED_PROFILE;
      
      const response = await apiClient.get(url);
      
      // Process profiles with signed URLs for profile pictures and banners
      if (response.data?.data && Array.isArray(response.data.data)) {
        const processedProfiles = await Promise.all(
          response.data.data.map(async (profile) => {
            const processedProfile = { ...profile };
            
            // Process profile_picture
            if (profile.profile_picture && !profile.profile_picture.startsWith('http')) {
              try {
                const signedUrlsData = await getSignedUrls([profile.profile_picture], true);
                if (signedUrlsData && signedUrlsData.length > 0) {
                  processedProfile.profile_picture = signedUrlsData[0].signedUrl;
                }
              } catch (error) {
                reportError(error, 'signed URL profile picture');
              }
            }
            
            // Process profile_banner
            if (profile.profile_banner && !profile.profile_banner.startsWith('http')) {
              try {
                const signedUrlsData = await getSignedUrls([profile.profile_banner], true);
                if (signedUrlsData && signedUrlsData.length > 0) {
                  processedProfile.profile_banner = signedUrlsData[0].signedUrl;
                }
              } catch (error) {
                reportError(error, 'signed URL profile banner');
              }
            }
            
            return processedProfile;
          })
        );
        
        response.data.data = processedProfiles;
      }
      
      return ResponseHandler.handleSuccess(response, 'Featured profiles fetched successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch featured profiles');
    }
  }

  /**
   * Fetch featured profiles for guest users
   * @param {Object} params - Query parameters object
   * @param {number} params.limit - Number of featured profiles to fetch
   * @param {string} params.category - Optional category filter
   * @param {string} params.sort - Optional sort parameter
   * @returns {Promise<Object>} Featured profiles response with signed URLs
   */
  async getFeaturedProfilesGuest(params = {}) {
    try {
      // Set default limit if not provided (show all featured)
      const queryParams = { limit: 50, ...params };
      
      // Build query string from params object
      const queryString = Object.keys(queryParams)
        .filter(key => queryParams[key] !== undefined && queryParams[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
        .join('&');
      
      const url = queryString 
        ? `${API_ENDPOINTS.FEATURED_PROFILE_GUEST}?${queryString}`
        : API_ENDPOINTS.FEATURED_PROFILE_GUEST;
      
      const response = await apiClient.get(url);
      
      // Process profiles with signed URLs for profile pictures and banners
      if (response.data?.data && Array.isArray(response.data.data)) {
        const processedProfiles = await Promise.all(
          response.data.data.map(async (profile) => {
            const processedProfile = { ...profile };
            
            // Process profile_picture
            if (profile.profile_picture && !profile.profile_picture.startsWith('http')) {
              try {
                const signedUrlsData = await getSignedUrls([profile.profile_picture], true);
                if (signedUrlsData && signedUrlsData.length > 0) {
                  processedProfile.profile_picture = signedUrlsData[0].signedUrl;
                }
              } catch (error) {
                reportError(error, 'signed URL profile picture');
              }
            }
            
            // Process profile_banner
            if (profile.profile_banner && !profile.profile_banner.startsWith('http')) {
              try {
                const signedUrlsData = await getSignedUrls([profile.profile_banner], true);
                if (signedUrlsData && signedUrlsData.length > 0) {
                  processedProfile.profile_banner = signedUrlsData[0].signedUrl;
                }
              } catch (error) {
                reportError(error, 'signed URL profile banner');
              }
            }
            
            return processedProfile;
          })
        );
        
        response.data.data = processedProfiles;
      }
      
      return ResponseHandler.handleSuccess(response, 'Featured profiles fetched successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch featured profiles');
    }
  }

  /**
   * Fetch recommended profiles for authenticated users
   * @param {Object} params - Query parameters object
   * @param {number} params.limit - Number of recommended profiles to fetch (default: 9)
   * @param {number} params.skip - Number of profiles to skip (default: 0)
   * @returns {Promise<Object>} Recommended profiles response with signed URLs
   */
  async getRecommendedProfiles(params = {}) {
    try {
      // Set default values
      const queryParams = { 
        limit: params.limit || 9, 
        skip: params.skip || 0 
      };
      
      // Build query string from params object
      const queryString = Object.keys(queryParams)
        .filter(key => queryParams[key] !== undefined && queryParams[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
        .join('&');
      
      const url = `${API_ENDPOINTS.RECOMMENDED_PROFILE}?${queryString}`;
      
      const response = await apiClient.get(url);
      
      // Process profiles with signed URLs for profile pictures
      if (response.data?.data && Array.isArray(response.data.data)) {
        const processedProfiles = await Promise.all(
          response.data.data.map(async (profile) => {
            const processedProfile = { ...profile };
            
            // Process profile_picture
            if (profile.profile_picture && !profile.profile_picture.startsWith('http')) {
              try {
                const signedUrlsData = await getSignedUrls([profile.profile_picture], true);
                if (signedUrlsData && signedUrlsData.length > 0) {
                  processedProfile.profile_picture = signedUrlsData[0].signedUrl;
                }
              } catch (error) {
                reportError(error, 'signed URL profile picture');
              }
            }
            
            return processedProfile;
          })
        );
        
        response.data.data = processedProfiles;
      }
      
      return ResponseHandler.handleSuccess(response, 'Recommended profiles fetched successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch recommended profiles');
    }
  }

  /**
   * Fetch trending profiles for authenticated users
   * @param {Object} params - Query parameters object
   * @param {number} params.limit - Number of trending profiles to fetch (default: 10)
   * @param {number} params.page - Page number (default: 0)
   * @returns {Promise<Object>} Trending profiles response with signed URLs
   */
  async getTrendingProfiles(params = {}) {
    try {
      // Set default values
      const queryParams = { 
        limit: params.limit || 10, 
        page: params.page || 0 
      };
      
      // Build query string from params object
      const queryString = Object.keys(queryParams)
        .filter(key => queryParams[key] !== undefined && queryParams[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
        .join('&');
      
      const url = `${API_ENDPOINTS.TRENDING_PROFILE}?${queryString}`;
      
      const response = await apiClient.get(url);
      
      // Process profiles with signed URLs for profile pictures and banners
      if (response.data?.data && Array.isArray(response.data.data)) {
        const processedProfiles = await Promise.all(
          response.data.data.map(async (profile) => {
            const processedProfile = { ...profile };
            
            // Process profile_picture
            if (profile.profile_picture && !profile.profile_picture.startsWith('http')) {
              try {
                const signedUrlsData = await getSignedUrls([profile.profile_picture], true);
                if (signedUrlsData && signedUrlsData.length > 0) {
                  processedProfile.profile_picture = signedUrlsData[0].signedUrl;
                }
              } catch (error) {
                reportError(error, 'signed URL profile picture');
              }
            }
            
            // Process profile_banner
            if (profile.profile_banner && !profile.profile_banner.startsWith('http')) {
              try {
                const signedUrlsData = await getSignedUrls([profile.profile_banner], true);
                if (signedUrlsData && signedUrlsData.length > 0) {
                  processedProfile.profile_banner = signedUrlsData[0].signedUrl;
                }
              } catch (error) {
                reportError(error, 'signed URL profile banner');
              }
            }
            
            return processedProfile;
          })
        );
        
        response.data.data = processedProfiles;
      }
      
      return ResponseHandler.handleSuccess(response, 'Trending profiles fetched successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch trending profiles');
    }
  }

  /**
   * Fetch trending profiles for guest users
   * @param {Object} params - Query parameters object
   * @param {number} params.limit - Number of trending profiles to fetch (default: 10)
   * @param {number} params.page - Page number (default: 0)
   * @returns {Promise<Object>} Trending profiles response with signed URLs
   */
  async getTrendingProfilesGuest(params = {}) {
    try {
      // Set default values
      const queryParams = { 
        limit: params.limit || 10, 
        page: params.page || 0 
      };
      
      // Build query string from params object
      const queryString = Object.keys(queryParams)
        .filter(key => queryParams[key] !== undefined && queryParams[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
        .join('&');
      
      const url = `${API_ENDPOINTS.TRENDING_PROFILE_GUEST}?${queryString}`;
      
      const response = await apiClient.get(url);
      
      // Process profiles with signed URLs for profile pictures and banners
      if (response.data?.data && Array.isArray(response.data.data)) {
        const processedProfiles = await Promise.all(
          response.data.data.map(async (profile) => {
            const processedProfile = { ...profile };
            
            // Process profile_picture
            if (profile.profile_picture && !profile.profile_picture.startsWith('http')) {
              try {
                const signedUrlsData = await getSignedUrls([profile.profile_picture], true);
                if (signedUrlsData && signedUrlsData.length > 0) {
                  processedProfile.profile_picture = signedUrlsData[0].signedUrl;
                }
              } catch (error) {
                reportError(error, 'signed URL profile picture');
              }
            }
            
            // Process profile_banner
            if (profile.profile_banner && !profile.profile_banner.startsWith('http')) {
              try {
                const signedUrlsData = await getSignedUrls([profile.profile_banner], true);
                if (signedUrlsData && signedUrlsData.length > 0) {
                  processedProfile.profile_banner = signedUrlsData[0].signedUrl;
                }
              } catch (error) {
                reportError(error, 'signed URL profile banner');
              }
            }
            
            return processedProfile;
          })
        );
        
        response.data.data = processedProfiles;
      }
      
      return ResponseHandler.handleSuccess(response, 'Trending profiles fetched successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch trending profiles');
    }
  }

  /**
   * Get subscription counts and expiring soon list for the current user
   * @returns {Promise<Object>} Subscription counts response
   */
  async getSubscriptionCounts() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.SUBSCRIPTION_COUNTS);
      
      // Process profile_picture in expiringSoonList
      if (response.data?.expiringSoonList && Array.isArray(response.data.expiringSoonList)) {
        const processedList = await Promise.all(
          response.data.expiringSoonList.map(async (item) => {
            const processedItem = { ...item };
            
            // Process profile_picture in creatorInfo if it exists
            if (processedItem.creatorInfo?.profile_picture && !processedItem.creatorInfo.profile_picture.startsWith('http')) {
              try {
                const signedUrlsData = await getSignedUrls([processedItem.creatorInfo.profile_picture], true);
                if (signedUrlsData && signedUrlsData.length > 0) {
                  processedItem.creatorInfo.profile_picture = signedUrlsData[0].signedUrl;
                }
              } catch (error) {
                reportError(error, 'signed URL profile picture');
              }
            }
            
            return processedItem;
          })
        );
        response.data.expiringSoonList = processedList;
      }
      
      return ResponseHandler.handleSuccess(response, 'Subscription counts fetched successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch subscription counts');
    }
  }

  /**
   * Get user subscriptions with pagination and filtering
   * @param {Object} params - Query parameters object
   * @param {number} params.limit - Number of subscriptions to fetch (default: 9)
   * @param {number} params.page - Page number (default: 0)
   * @param {string} params.filter - Optional filter ('subscription', 'follow', 'expire', 'expiresoon')
   * @returns {Promise<Object>} Subscriptions response with data, pagination info, and signed URLs for profile pictures
   */
  async getUserSubscriptions(params = {}) {
    try {
      // Set default values
      const queryParams = {
        limit: params.limit || 9,
        skip: params.page || 0
      };

      // Add filter if provided
      if (params.filter) {
        queryParams.filter = params.filter;
      }

      // Build query string from params object
      const queryString = Object.keys(queryParams)
        .filter(key => queryParams[key] !== undefined && queryParams[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
        .join('&');

      const url = `${API_ENDPOINTS.SUBSCRIPTIONS}?${queryString}`;
      const response = await apiClient.get(url);

      // Process subscriptions with signed URLs for profile pictures
      if (response.data?.data && Array.isArray(response.data.data)) {
        const profileKeys = response.data.data
          .map(subscription => subscription.ccreator?.profile_picture)
          .filter(key => key && typeof key === 'string' && !key.startsWith('http'));

        let urlMap = {};
        if (profileKeys.length > 0) {
          try {
            const signed = await getSignedUrls(profileKeys, true);
            if (Array.isArray(signed)) {
              signed.forEach(({ key, signedUrl }) => {
                urlMap[key] = signedUrl;
              });
            } else if (signed && typeof signed === 'object') {
              urlMap = { ...signed };
            }
          } catch (error) {
            reportError(error, 'signed URLs profile pictures');
          }
        }

        // Update profile pictures with signed URLs
        response.data.data = response.data.data.map(subscription => {
          const profilePicture = subscription.ccreator?.profile_picture;
          if (profilePicture && urlMap[profilePicture]) {
            return {
              ...subscription,
              ccreator: {
                ...subscription.ccreator,
                profile_picture: urlMap[profilePicture]
              }
            };
          }
          return subscription;
        });
      }

      // Preserve the full response structure with pagination info
      // The API returns: { data: [...], totalPages, recordsTotal, perPage, currentPage }
      // ResponseHandler.handleSuccess would extract just the array, losing pagination
      // So we manually construct the response following the same pattern
      return {
        success: true,
        status: response.status || 200,
        message: 'Subscriptions fetched successfully',
        data: response.data, // Preserve full structure with data array and pagination fields
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch subscriptions');
    }
  }

}

export default new UserService();
